/**
 * automationDataCatalog.js
 *
 * Builds the "dynamic data" catalog shown in the automation value editor
 * (TokenValueEditor popover). Every category is built from REAL sources:
 *
 * - trigger:    structure fields (the `fields` prop already used by the editor)
 * - references: the flattened reference fields the engine injects into
 *               elementDataFormatted (see automationEngine.executeWorkflow)
 * - system:     exactly what the backend resolveFieldPath supports, no more
 * - dates:      the supported date-tag grammar (todayDate / invoiceDate)
 * - async:      tokens requiring a Firestore lookup at runtime (badged)
 * - steps:      outputVariables of steps BEFORE the current one
 *
 * SECURITY: never suggests credentials or secrets. `isSensitiveTokenPath`
 * filters out anything that looks like a credential (defense in depth — those
 * paths should never be in `fields` to begin with).
 *
 * Tokens use the canonical insertion syntaxes:
 * - fields / references / system / dates / async → {{path}} (string template)
 * - step outputs → $var (type-preserving when used alone)
 */

import { toElementFieldPath } from './automationVariables';
import { getAIOutputCatalogType } from './automationAI';

const CREDENTIAL_PATTERNS = [
  /oauth/i,
  /secret/i,
  /apikey/i,
  /api_key/i,
  /password/i,
  /credential/i,
  /accesstoken/i,
  /refreshtoken/i,
  /connectorsinstalled/i,
];

export function isSensitiveTokenPath(path) {
  return CREDENTIAL_PATTERNS.some((re) => re.test(String(path || '')));
}

const tr = (lang, fr, en) => (lang === 'fr' ? fr : en);

/**
 * Flattened reference fields injected by the engine into elementDataFormatted.
 * Source of truth: automationEngine.executeWorkflow (backend).
 */
const REFERENCE_FIELDS = [
  { key: 'name', fr: "Nom de l'élément", en: 'Element name' },
  { key: 'documentIdentifiant', fr: "Identifiant de l'élément", en: 'Element identifier' },
  { key: 'displayCode', fr: "Code d'affichage", en: 'Display code' },
  { key: 'dependencyDisplayCode', fr: 'Code du parent', en: 'Parent display code' },
  { key: 'assignedToId', fr: 'Assigné — ID', en: 'Assignee — ID' },
  { key: 'assignedToName', fr: 'Assigné — Nom', en: 'Assignee — Name' },
  { key: 'assignedToPath', fr: 'Assigné — Chemin', en: 'Assignee — Path' },
  { key: 'dependencyId', fr: 'Parent — ID', en: 'Parent — ID' },
  { key: 'dependencyName', fr: 'Parent — Nom', en: 'Parent — Name' },
  { key: 'dependencyPath', fr: 'Parent — Chemin', en: 'Parent — Path' },
  { key: 'targetId', fr: 'Cible — ID', en: 'Target — ID' },
  { key: 'targetName', fr: 'Cible — Nom', en: 'Target — Name' },
  { key: 'targetPath', fr: 'Cible — Chemin', en: 'Target — Path' },
  { key: 'targetProfileId', fr: 'Profil cible — ID', en: 'Target profile — ID' },
  { key: 'targetProfileName', fr: 'Profil cible — Nom', en: 'Target profile — Name' },
  { key: 'targetProfilePath', fr: 'Profil cible — Chemin', en: 'Target profile — Path' },
  { key: 'structureId', fr: 'Structure — ID', en: 'Structure — ID' },
  { key: 'structurePath', fr: 'Structure — Chemin', en: 'Structure — Path' },
  { key: 'ownerId', fr: 'Business — ID', en: 'Business — ID' },
  { key: 'ownerName', fr: 'Business — Nom', en: 'Business — Name' },
  { key: 'collectionField', fr: 'Collection', en: 'Collection' },
];

const FINANCE_FIELDS = [
  'total',
  'subtotal',
  'balance',
  'amount',
  'price',
  'fees',
  'tax1',
  'tax2',
  'tax3',
];

/**
 * Flatten a declared `outputs` schema (registry) into dot-paths relative to
 * the step RESULT object (so paths start with `data.`), depth-limited.
 * Example: { properties: { Invoice: { properties: { Id: {} } } } }
 * → ['data.Invoice', 'data.Invoice.Id']
 */
export function flattenOutputPaths(outputs, prefix = 'data', depth = 3) {
  const paths = [];
  const walk = (node, base, remaining) => {
    if (!node || typeof node !== 'object' || remaining <= 0) return;
    Object.entries(node.properties || {}).forEach(([key, child]) => {
      const path = `${base}.${key}`;
      paths.push({ path, type: child.type, label: child.label });
      walk(child, path, remaining - 1);
    });
  };
  walk(outputs, prefix, depth);
  return paths;
}

/**
 * Find the declared outputs schema of a connectorAction step, if any.
 */
function findStepOutputs(step, connectorsAvailable) {
  if (step?.type !== 'connectorAction') return null;
  const connector = (connectorsAvailable || []).find(
    (c) => c.id === step.config?.connectorId
  );
  const actions = Array.isArray(connector?.actions) ? connector.actions : [];
  const action = actions.find((a) => a.key === step.config?.actionKey);
  return action?.outputs || null;
}

/**
 * Build the catalog categories.
 *
 * @param {Object} options
 * @param {Array}  options.fields        - structure fields ({ value, name, typeData })
 * @param {Array}  options.steps         - all automation steps
 * @param {string} options.currentStepId - id of the step being edited
 * @param {string} options.lang          - 'fr' | 'en'
 * @param {Array}  options.connectorsAvailable - GET connectors payload (to
 *                 enrich previous connectorAction steps with declared outputs)
 * @returns {Array<{ id, label, items: Array }>}
 */
export function buildAutomationDataCatalog({
  fields = [],
  steps = [],
  currentStepId = null,
  lang = 'fr',
  connectorsAvailable = [],
} = {}) {
  const categories = [];

  // ── Trigger element fields ────────────────────────────────────────────
  const triggerItems = (fields || [])
    .map((f) => {
      // Resolve to the real Firestore path: the engine reads tokens straight
      // off elementData, where structure fields live under `data.*`.
      const key = toElementFieldPath(f);
      if (!key || isSensitiveTokenPath(key)) return null;
      return {
        id: `trigger:${key}`,
        token: `{{${key}}}`,
        label: f.name || key,
        type: f.typeData || undefined,
        category: 'trigger',
      };
    })
    .filter(Boolean);
  if (triggerItems.length > 0) {
    categories.push({
      id: 'trigger',
      label: tr(lang, 'Déclencheur', 'Trigger'),
      items: triggerItems,
    });
  }

  // ── Flattened references + finances ──────────────────────────────────
  const referenceItems = [
    ...REFERENCE_FIELDS.map((f) => ({
      id: `ref:${f.key}`,
      token: `{{${f.key}}}`,
      label: tr(lang, f.fr, f.en),
      category: 'references',
    })),
    ...FINANCE_FIELDS.map((key) => ({
      id: `ref:finances.${key}`,
      token: `{{finances.${key}}}`,
      label: `Finances — ${key}`,
      badge: 'amount',
      note: tr(
        lang,
        'Montant déjà divisé par 10 000 (valeur affichable).',
        'Amount already divided by 10,000 (display value).'
      ),
      category: 'references',
    })),
  ];
  categories.push({
    id: 'references',
    label: tr(lang, "Références de l'élément", 'Element references'),
    items: referenceItems,
  });

  // ── Previous step outputs ─────────────────────────────────────────────
  const currentIndex = steps.findIndex((s) => s.id === currentStepId);
  const stepItems = [];
  if (currentIndex > 0) {
    for (let i = 0; i < currentIndex; i += 1) {
      const step = steps[i];
      const outputVars = step?.outputVariables || {};
      const stepName = step?.name || `${tr(lang, 'Étape', 'Step')} ${i + 1}`;
      const declaredOutputs = findStepOutputs(step, connectorsAvailable);
      Object.entries(outputVars).forEach(([varName, mappedPath]) => {
        stepItems.push({
          id: `step:${i}:${varName}`,
          token: `$${varName}`,
          label: varName,
          stepName,
          ...(step.type === 'askAI' && mappedPath === 'value'
            ? { type: getAIOutputCatalogType(step, steps, fields) } : {}),
          typePreserving: true,
          category: 'steps',
        });
        if (mappedPath === 'selected') {
          stepItems.push({
            id: `step:${i}:${varName}.documentPath`,
            token: `$${varName}.documentPath`,
            label: `${varName}.documentPath`,
            stepName,
            typePreserving: true,
            category: 'steps',
          });
          stepItems.push({
            id: `step:${i}:${varName}.id`,
            token: `$${varName}.id`,
            label: `${varName}.id`,
            stepName,
            typePreserving: true,
            category: 'steps',
          });
          const connectorId =
            step?.type === 'queryCell' ? step.config?.connectorId : '';
          if (connectorId) {
            stepItems.push({
              id: `step:${i}:${varName}.${connectorId}.id`,
              token: `$${varName}.${connectorId}.id`,
              label: `${varName}.${connectorId}.id`,
              stepName,
              typePreserving: true,
              category: 'steps',
            });
          }
        }
        // Declared connector outputs: when the variable captures the whole
        // `data` result, offer the known sub-properties too.
        if (declaredOutputs && mappedPath === 'data') {
          flattenOutputPaths(declaredOutputs).forEach(({ path, type, label }) => {
            const subPath = path.replace(/^data\./, '');
            stepItems.push({
              id: `step:${i}:${varName}.${subPath}`,
              token: `$${varName}.${subPath}`,
              label: label?.[lang] || `${varName}.${subPath}`,
              stepName,
              type,
              typePreserving: true,
              category: 'steps',
            });
          });
        }
      });
    }
  }
  if (stepItems.length > 0) {
    categories.push({
      id: 'steps',
      label: tr(lang, 'Étapes précédentes', 'Previous steps'),
      items: stepItems,
    });
  }

  // ── System tags (exact inventory of resolveFieldPath) ────────────────
  categories.push({
    id: 'system',
    label: tr(lang, 'Système', 'System'),
    items: [
      {
        id: 'system:elementId',
        token: '{{elementId}}',
        label: tr(lang, "ID de l'élément", 'Element ID'),
        category: 'system',
      },
      {
        id: 'system:workflowId',
        token: '{{workflowId}}',
        label: tr(lang, "ID de l'automatisation", 'Automation ID'),
        category: 'system',
      },
      {
        id: 'system:businessId',
        token: '{{businessId}}',
        label: tr(lang, 'ID du business', 'Business ID'),
        category: 'system',
      },
    ],
  });

  // ── Date tags ─────────────────────────────────────────────────────────
  categories.push({
    id: 'dates',
    label: tr(lang, 'Dates', 'Dates'),
    items: [
      {
        id: 'date:today',
        token: '{{todayDate}}',
        label: tr(lang, "Aujourd'hui", 'Today'),
        category: 'dates',
      },
      {
        id: 'date:today+7',
        token: '{{todayDate+7}}',
        label: tr(lang, "Aujourd'hui +7 jours", 'Today +7 days'),
        note: tr(lang, 'Ajustez le décalage (+N / -N).', 'Adjust the offset (+N / -N).'),
        category: 'dates',
      },
      {
        id: 'date:todayZ',
        token: '{{todayDate*Z*}}',
        label: tr(lang, "Aujourd'hui (horodatage ISO)", 'Today (ISO timestamp)'),
        category: 'dates',
      },
      {
        id: 'date:tomorrow8',
        token: '{{todayDate+1@08:00}}',
        label: tr(lang, 'Demain à 08:00', 'Tomorrow at 08:00'),
        note: tr(lang, 'Heure en fuseau America/Toronto.', 'Time in America/Toronto timezone.'),
        category: 'dates',
      },
      {
        id: 'date:invoice',
        token: '{{invoiceDate}}',
        label: tr(lang, 'Date de facture', 'Invoice date'),
        note: tr(
          lang,
          "Champ invoiceDate de l'élément (aujourd'hui si absent).",
          "Element's invoiceDate field (today if absent).",
        ),
        category: 'dates',
      },
      {
        id: 'date:invoice+30',
        token: '{{invoiceDate+30}}',
        label: tr(lang, 'Date de facture +30 jours', 'Invoice date +30 days'),
        category: 'dates',
      },
    ],
  });

  // ── Async tokens (Firestore lookup at runtime) ────────────────────────
  categories.push({
    id: 'async',
    label: tr(lang, 'Avancé (asynchrone)', 'Advanced (async)'),
    items: [
      {
        id: 'async:assignedToEmail',
        token: '{{assignedToEmail}}',
        label: tr(lang, "Courriel de l'assigné", "Assignee's email"),
        badge: 'async',
        category: 'async',
      },
      {
        id: 'async:dependencyEmail',
        token: '{{dependency.data.Cou_field271}}',
        label: tr(lang, 'Courriel du parent (Cou_field271)', 'Parent email (Cou_field271)'),
        badge: 'async',
        category: 'async',
      },
    ],
  });

  return categories;
}

/**
 * Flat search across all categories (accent-insensitive, matches label,
 * token and step name).
 */
export function searchCatalog(categories, query) {
  const normalized = String(query || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  if (!normalized) return categories;

  return categories
    .map((cat) => ({
      ...cat,
      items: cat.items.filter((item) => {
        const haystack = `${item.label} ${item.token} ${item.stepName || ''}`
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '');
        return haystack.includes(normalized);
      }),
    }))
    .filter((cat) => cat.items.length > 0);
}
